import React from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Snackbar,
  makeStyles,
  Container,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { BarChart } from "@material-ui/icons";
import {
  CommissionProvider,
  useCommissionContext,
} from "../../../../contexts/CommissionContext";
import CommissionFreezeControl from "./CommissionsFreezeControl";
import CutoffDateSetting from "./CutoffDateSetting";
import ExportTab from "./ExportTab";
import ImportTab from "./ImportTab";
import PostProcessingTab from "./PostProcessingTab";
import StructureOperations from "./StructureOperations";
import SalesReportTab from "./ReportTab/SalesReportTab";

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: "100%",
    padding: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(4),
    fontWeight: 700,
  },
  controlsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      "& > *": {
        flex: 1,
      },
    },
  },
  tabsContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabContent: {
    padding: theme.spacing(1),
  },
}));

/**
 * Component for rendering the content inside a tab.
 * @param {object} props - The properties for the TabPanel.
 * @param {React.ReactNode} props.children - The content to display inside the tab.
 * @param {number} props.value - The current active tab index.
 * @param {number} props.index - The index of this tab.
 * @returns {React.Element} The rendered tab panel component.
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`commission-tabpanel-${index}`}
      aria-labelledby={`commission-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

/**
 * CommissionsPageContent component renders the main content for managing commissions.
 * It includes controls for freezing commissions and setting cutoff dates, as well as
 * tabs for exporting, importing, and post-processing commission data. It also displays
 * a snackbar for notifications.
 *
 * @component
 * @returns {React.Element} The rendered component for commission management.
 */

/**
 *
 */
function CommissionsPageContent() {
  const classes = useStyles();
  const { activeTab, setActiveTab, snackbar, handleCloseSnackbar } =
    useCommissionContext();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" component="h1" gutterBottom>
          Commissions Management
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Manage commission settings, calculations, and exports
        </Typography>
      </Box>

      {/* Controls section */}
      <Box className={classes.controlsContainer}>
        <CommissionFreezeControl />
        <CutoffDateSetting />
      </Box>

      <StructureOperations />

      {/* Main tabs */}
      <Box className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          className={classes.tabs}
        >
          <Tab icon={<GetAppIcon />} label="Export Data" />
          <Tab icon={<PublishIcon />} label="Import Data" />
          <Tab icon={<PlayArrowIcon />} label="Post-Processing" />
          <Tab icon={<BarChart />} label="Sales Report" />
        </Tabs>

        <Box className={classes.tabContent}>
          <TabPanel value={activeTab} index={0}>
            <ExportTab />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <ImportTab />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <PostProcessingTab />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <SalesReportTab />
          </TabPanel>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

// Wrap with provider
/**
 *
 */
export default function CommissionsPage() {
  return (
    <CommissionProvider>
      <CommissionsPageContent />
    </CommissionProvider>
  );
}
